import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Skeleton, Space, Stack, Text } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { useLingui } from '@lingui/react';
import { DocumentDto } from '../../../Service/DocumentT';
import { QuestionnaireS } from '../../../Service/QuestionnaireS';
import BookitupTable from '../../../Atoms/BookitupTable';
import QuestionnaireItem from '../../../Molecules/Event/Questionnaires/QuestionnaireItem';
import PanePaper from '../../../Molecules/PanePaper';
import { QuestionnaireDto } from '../../../Types/QuestionnaireT';
import DocumentListItem from '../../../Molecules/Event/Documents/DocumentListItem';
import { DocumentS } from '../../Documents/DocumentS';
import BookitupDocument, { BookitupContract } from '../../../Types/BookitupDocument';
import ContractItem from '../../../Molecules/Event/Contracts/ContractItem';
import BookitupTutorialIcon from '../../../Atoms/BookitupTutorialIcon';
import BookitupAddIcon from '../../../Atoms/BookitupAddIcon';
import { TestS } from '../../../TestS';
import { openCreateDocumentModal } from '../../../Molecules/Event/Documents/CreateDocumentModal';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import { useModalState } from '../../../Molecules/Dialogs';
import SelectQuestionnaireTemplateModal from '../../../Molecules/Settings/QuestionnaireTemplate/SelectQuestionnaireTemplateModal';
import { newQuestionnaire } from '../../../Service/restapi/questionnaireService';
import { ConstantS } from '../../../Service/ConstantS';
import { MixpanelS } from '../../../Service/MixpanelS';
import SelectContractTemplateView from '../../../Organismns/Contract/SelectContractTemplateView';
import { createContract } from '../../../Service/restapi/contractService';
import { ToastS } from '../../../Service/ToastS';

const ContactDocuments = () => (
  <Stack>
    <RegularDocumentsList />
    <ContractsList />
    <QuestionnairesList />
  </Stack>
);

export default ContactDocuments;

export const RegularDocumentsList = () => {
  const { id: contactId } = useParams();
  const { i18n } = useLingui();
  const { openLicenceModal } = useContext(ViolationContext);
  const [isLoading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [sortedDocuments, setSortedDocuments] = useState();
  const { ref, width } = useElementSize();

  const showColumns = {
    showNumber: width > 490,
    showDatum: width > 540,
    showName: false,
    showDueDate: width > 400,
    showPortal: width >= 240,
    narrowPortalStatus: width < 290,
    showSum: true,
    showEvent: width > 760,
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    if (documents) {
      const tmpSortedDocuments: any = {};
      ['OFFER', 'CONFIRMATION', 'PARTIAL_INVOICE', 'INVOICE', 'REMINDER', 'CANCEL'].forEach((docType) => {
        const filteredDocs = documents.filter((d) => d.documentType === docType);
        if (filteredDocs.length > 0) {
          tmpSortedDocuments[docType] = filteredDocs;
        }
      });
      setSortedDocuments(tmpSortedDocuments);
    }
  }, [documents]);

  const fetchDocuments = async () => {
    if (contactId) {
      setLoading(true);
      const resJSON = await DocumentS.fetchDocumentsByCustomerContactId(Number(contactId));
      if (resJSON) {
        setDocuments(resJSON);
      }
      setLoading(false);
    }
  };

  const addDocument = () => {
    if (contactId) {
      openCreateDocumentModal({
        initialCustomerId: Number(contactId),
        openLicenceModal,
        provideEventSelect: true,
        disableCustomerSelection: true,
      });
    }
  };

  return (
    <PanePaper
      title="common.documents"
      tooltip="help.event.documents.pane"
      rightSection={
        <>
          <BookitupTutorialIcon
            videoUrl="https://app.bookitup.de/academy/channels/offer-service?episodeId=tfl5yj1fg2"
            name="CreateDocument"
          />
          <BookitupAddIcon onClick={addDocument} testId={TestS.Documents.CREATE_DOCUMENT} />
        </>
      }
    >
      <div ref={ref}>
        {isLoading ? (
          <div>
            {Array.from(Array(3)).map(() => (
              <Skeleton key={Math.random()} radius="md" height={25} width="100%" mb={10} />
            ))}
          </div>
        ) : (
          <div>
            {!documents || !sortedDocuments || documents.length === 0 ? (
              <Text italic color="dimmed" size="xs" weight="lighter">
                Noch nichts hinzugefügt.
              </Text>
            ) : (
              <BookitupTable>
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>Status</th>
                    {showColumns.showNumber && <th style={{ width: 50 }}>Nummer</th>}
                    {showColumns.showDatum && <th style={{ width: 50 }}>Datum</th>}
                    <th style={{ width: 100 }}>Betrag</th>
                    {showColumns.showDueDate && <th style={{ width: 100 }}>Frist</th>}
                    {showColumns.showEvent && <th style={{ width: 225 }}>Event</th>}
                    {showColumns.showPortal && (
                      <th style={{ width: showColumns.narrowPortalStatus ? 50 : 120 }}>Portal</th>
                    )}
                    <th> </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(sortedDocuments).map((key) => {
                    const docs: any = sortedDocuments[key];
                    return (
                      <>
                        <tr className="notHover" style={{ backgroundColor: 'transparent' }}>
                          <td colSpan={6}>
                            <Text weight="bolder">
                              <b> {i18n._(`document.${key.toLowerCase()}`)}</b>
                            </Text>
                          </td>
                        </tr>
                        {docs.map((doc: any) => (
                          <DocumentListItem
                            key={doc.id}
                            withEventStatus={showColumns.showEvent}
                            bookitupDocument={doc}
                            reload={() => fetchDocuments()}
                            {...showColumns}
                          />
                        ))}
                        <Space h="md" />
                      </>
                    );
                  })}
                </tbody>
              </BookitupTable>
            )}
          </div>
        )}
      </div>
    </PanePaper>
  );
};

const ContractsList = () => {
  const { id: contactId } = useParams();
  const [isLoading, setLoading] = useState(false);
  const { show, toggle } = useModalState();
  const { executeWithLicenceCheck, interceptViolation } = useContext(ViolationContext);
  const [contracts, setContracts] = useState<BookitupContract[]>();
  const navigate = useNavigate();

  // const navigate = useNavigate();
  const { ref, width } = useElementSize();

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    if (contactId) {
      setLoading(true);
      const resJSON = await DocumentS.filterContracts(undefined, Number(contactId));
      if (resJSON) {
        setContracts(resJSON);
      }
      setLoading(false);
    }
  };

  const create = async (templateName: string, bookingDocument: BookitupDocument | null, eventId: string) => {
    setLoading(true);
    if (bookingDocument && bookingDocument.draft) {
      const resJSON = await DocumentS.patchDocument(bookingDocument.id, { draft: false }, interceptViolation);
      if (!resJSON) {
        setLoading(false);
        return;
      }
    }
    createContract(contactId, templateName, eventId, bookingDocument ? bookingDocument.id : undefined)
      .then((resp) => {
        toggle();
        if (resp.ok) {
          resp.json().then((newContract) => {
            MixpanelS.track(ConstantS.TrackingEvents.ContractCreated);
            navigate(`/events/${eventId}/contracts/${newContract.id}`);
          });
        } else {
          ToastS.generalError();
        }
      })
      .finally(() => setLoading(false));
  };

  const showColumns = {
    showNumber: width > 300,
    showDatum: width > 350,
    showName: false,
    showLabel: width > 500,
    narrowPortalStatus: width < 200,
    showPortal: width >= 150,
    showEvent: width > 720,
  };

  return (
    <PanePaper
      title="common.contracts"
      tooltip="help.event.contracts-offline.pane"
      rightSection={
        <>
          <BookitupTutorialIcon
            videoUrl="https://app.bookitup.de/academy/channels/offer-service?episodeId=ye6osph0pm"
            name="CreateContract"
          />
          <BookitupAddIcon onClick={() => executeWithLicenceCheck(LicenceS.Restrictions.CONTRACT_USAGE, toggle)} />
        </>
      }
    >
      <div ref={ref}>
        {isLoading ? (
          <div>
            {Array.from(Array(3)).map(() => (
              <Skeleton key={Math.random()} radius="md" height={25} width="100%" mb={10} />
            ))}
          </div>
        ) : (
          <div>
            {!contracts || contracts.length === 0 ? (
              <Text italic color="dimmed" size="xs" weight="lighter">
                Noch nichts hinzugefügt.
              </Text>
            ) : (
              <BookitupTable>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>Status</th>
                    {showColumns.showNumber && <th style={{ width: 50 }}>Nummer</th>}
                    {showColumns.showLabel && <th style={{ width: 100 }}>Name</th>}
                    {showColumns.showName && <th>Vertragspartner:in</th>}
                    {showColumns.showDatum && <th style={{ width: 50 }}>Datum</th>}
                    {showColumns.showEvent && <th style={{ width: 220 }}>Event</th>}
                    {showColumns.showPortal && (
                      <th style={{ width: showColumns.narrowPortalStatus ? 50 : 120 }}>Portal</th>
                    )}
                    <th> </th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((contract) => (
                    <ContractItem
                      key={contract.id}
                      contract={contract}
                      reload={() => fetchContracts()}
                      {...showColumns}
                    />
                  ))}
                </tbody>
              </BookitupTable>
            )}
          </div>
        )}
      </div>
      <SelectContractTemplateView
        open={show}
        toggle={toggle}
        isLoading={isLoading}
        finish={create}
        eventId={undefined}
        provideEventSelect
        contactId={contactId}
      />
    </PanePaper>
  );
};

const QuestionnairesList = () => {
  const { id: contactId } = useParams();
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireDto[]>([]);
  const [loading, setLoading] = useState(false);
  const { show, toggle } = useModalState(false);
  const navigate = useNavigate();
  const { ref, width } = useElementSize();

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const load = async () => {
    if (contactId) {
      setLoading(true);
      const resJSON = await QuestionnaireS.fetchQuestionnairesByCustomerId(Number(contactId));
      if (resJSON) {
        setQuestionnaires(resJSON);
      }
      setLoading(false);
    }
  };

  const addNewQuestionnaire = (templateName: string, eventId: string) => {
    setLoading(true);
    newQuestionnaire(eventId, contactId, templateName)
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((questionnaire) => {
            MixpanelS.track(ConstantS.TrackingEvents.QuestionnaireCreated);
            navigate(`/events/${eventId}/questionnaires/${questionnaire.id}`);
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const showColumns = {
    showDatum: width > 350,
    showPortal: width >= 230,
    narrowPortalStatus: width < 280,
  };

  return (
    <PanePaper
      showTitle
      title="common.questionnaires"
      tooltip="licence.event.questionnaires-offline.pane"
      rightSection={
        <>
          <BookitupTutorialIcon
            videoUrl={'https://app.bookitup.de/academy/channels/event-details?episodeId=zgkqf33ydb'}
            name={'CreateQuestionnaire'}
          />
          <BookitupAddIcon onClick={() => executeWithLicenceCheck(LicenceS.Restrictions.FRAGEBOGEN_USAGE, toggle)} />
        </>
      }
    >
      <div ref={ref}>
        {loading ? (
          <div>
            {Array.from(Array(3)).map(() => (
              <Skeleton key={Math.random()} radius="md" height={25} width="100%" mb={10} />
            ))}
          </div>
        ) : (
          <div>
            {questionnaires.length === 0 ? (
              <Text italic color="dimmed" size="xs" weight="lighter">
                Noch nichts hinzugefügt.
              </Text>
            ) : (
              <BookitupTable>
                <thead>
                  <tr>
                    <th>Bezeichnung</th>
                    {showColumns.showDatum && <th style={{ width: 50 }}>Datum</th>}
                    {showColumns.showPortal && (
                      <th style={{ width: showColumns.narrowPortalStatus ? 50 : 180 }}>Portal</th>
                    )}
                    <th> </th>
                  </tr>
                </thead>
                <tbody>
                  {questionnaires.map((q) => (
                    <QuestionnaireItem key={q.id} questionnaire={q} reload={() => load()} {...showColumns} />
                  ))}
                </tbody>
              </BookitupTable>
            )}
          </div>
        )}
      </div>
      <SelectQuestionnaireTemplateModal
        provideEventSelect
        isLoading={loading}
        open={show}
        toggle={toggle}
        finish={addNewQuestionnaire}
        contactId={contactId}
      />
    </PanePaper>
  );
};
